import { useState, useEffect, useRef, useCallback } from 'react'
import { config } from '../config/environment'
import { parseJwtPayload, tokenStillValid, tokenExpMs } from '@reachinsight-app/shared/src/utils/jwt'
import { useFeatureFlag, usePostHog } from '@reachinsight-app/shared/src'
import { isRivalOAuthEnabled } from '../authMode'

interface AuthState {
  token: string | null
  email: string | null
  name: string | null
  userId: string | null
  ready: boolean
  error: string | null
  /** Local/dev: show the user picker after logout (or when no auto-login). */
  needsLogin: boolean
}

type AuthSource = 'studio' | 'dev' | 'email-code' | null

export interface UseAuthResult extends AuthState {
  /** Force a token renewal now (used by the 401-retry net). Resolves to the fresh token, or null
   *  when renewal failed (a bounce/CTA is already in motion in that case). Single-flight. */
  refresh: () => Promise<string | null>
  /** End the Ros session (cookie + server record + per-tab bearer) and land on the signed-out screen. */
  logout: () => Promise<void>
  /** Start Rival login from the signed-out screen (clears the signed-out latch first). */
  signIn: () => void
}

/**
 * Resolve and MAINTAIN authentication for the Ros app.
 *
 * Acquisition (three paths, as before):
 * 1. Token in URL param (?token=...) — from Studio/Rival redirect. Now exp-VALIDATED before trust.
 * 2. Dev auth — local development with reachy-cf dev auth.
 * 3. No token — auth-required screen (now with a "Continue via Studio" CTA in AppShell).
 *
 * Renewal (new — fixes the session-expiry bug): Cognito access tokens live 60 minutes and Ros used
 * to hold exactly one, so every session died within the hour (or instantly, when handed a stale one).
 * For Studio-sourced tokens this hook now silently renews via POST {STUDIO_API_URL}/api/auth/refresh
 * (credentials included — the browser attaches Studio's cookies; Studio performs the Cognito refresh
 * grant and returns a fresh token; Ros NEVER sees the refresh token):
 *  - proactively, at exp − 2 min (timer), re-checked on tab focus/visibility (sleeping tabs miss timers)
 *  - reactively, via refresh() when a request 401s (AppShell's authFetch retries once)
 *  - on renewal failure with a live token: jittered retry; with a dead one: top-level bounce through
 *    Studio (/chat?return=<here>) which re-hands a fresh token — loop-guarded to avoid bounce cycles.
 * Dev-sourced tokens (local dev-login, 8h) get NO renewal machinery — dev flows are untouched.
 */
export function useAuth(): UseAuthResult {
  const [state, setState] = useState<AuthState>({
    token: null,
    email: null,
    name: null,
    userId: null,
    ready: false,
    error: null,
    needsLogin: false,
  })
  // Studio renews through Studio, email-code through Ros's Better Auth session; dev has no renewal.
  const sourceRef = useRef<AuthSource>(null)
  const tokenRef = useRef<string | null>(null)
  const inFlightRef = useRef<Promise<string | null> | null>(null)
  const retryTimerRef = useRef<number | null>(null)

  useEffect(() => { tokenRef.current = state.token }, [state.token])

  const bounceToStudio = useCallback(() => {
    // Loop guard: if we bounced less than a minute ago and are somehow here again, Studio isn't
    // producing usable tokens — show the CTA screen instead of ping-ponging the browser.
    const last = Number(sessionStorage.getItem(BOUNCE_KEY) || 0)
    if (Date.now() - last < 60_000) {
      setState(s => ({ ...s, token: null, ready: true, needsLogin: false, error: "Let's get you signed in again. This only takes a moment." }))
      return
    }
    try { sessionStorage.setItem(BOUNCE_KEY, String(Date.now())) } catch { /* storage unavailable */ }
    const here = window.location.pathname + window.location.search
    window.location.href = `${config.STUDIO_URL}/chat?return=${encodeURIComponent(here)}`
  }, [])

  const refresh = useCallback((): Promise<string | null> => {
    if (sourceRef.current !== 'studio' && sourceRef.current !== 'email-code') return Promise.resolve(null)
    if (inFlightRef.current) return inFlightRef.current // single-flight: concurrent 401s share one renewal
    inFlightRef.current = (async () => {
      try {
        const emailCodeSession = sourceRef.current === 'email-code'
        const res = await fetch(
          emailCodeSession ? `${config.API_CF_URL}/auth/session` : `${config.STUDIO_API_URL}/api/auth/refresh`,
          emailCodeSession ? { credentials: 'include' } : { method: 'POST', credentials: 'include' },
        )
        if (res.status === 401 || res.status === 403) {
          if (emailCodeSession) {
            try { sessionStorage.removeItem(TOKEN_KEY) } catch { /* storage unavailable */ }
            setState(emailCodeLoginState('Your sign-in has expired. Enter your email to continue.').state)
            return null
          }
          // Refresh token dead, or cookies not reaching Studio (3P-cookie blocking) — full re-handoff.
          bounceToStudio()
          return null
        }
        if (!res.ok) throw new Error(`refresh ${res.status}`)
        const data = await res.json() as { token?: string }
        if (!data.token) throw new Error('refresh response had no token')
        try { sessionStorage.setItem(TOKEN_KEY, data.token) } catch { /* storage unavailable */ }
        setState(authStateFromToken(data.token)) // every consumer re-binds via auth.token deps
        return data.token
      } catch {
        // Network / 5xx: retry with jitter while the current token still breathes; otherwise bounce.
        if (tokenRef.current && tokenStillValid(tokenRef.current)) {
          if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current)
          retryTimerRef.current = window.setTimeout(() => { void refresh() }, 30_000 + Math.random() * 30_000)
        } else {
          bounceToStudio()
        }
        return null
      } finally {
        inFlightRef.current = null
      }
    })()
    return inFlightRef.current
  }, [bounceToStudio])

  const logout = useCallback(async (): Promise<void> => {
    markSignedOut()
    try {
      await fetch(`${config.API_CF_URL}/auth/logout`, { method: 'POST', credentials: 'include' })
    } catch {
      // Cookie may still be live; the signed-out latch still blocks auto-resume.
    }
    // Drop any ?email= / ?token= so Path 2 cannot immediately re-login the same user.
    window.location.assign('/')
  }, [])
  // The OAuth front door (Ros's own Rival login) is gated on this PostHog flag. Off = the app
  // behaves exactly as before the OAuth work (CTA-to-Studio landing). We MUST wait for flags to
  // LOAD before resolving auth: useFeatureFlag returns false while loading, so resolving early
  // would send an entitled user down the legacy CTA path by mistake (mirrors main.tsx FlagRoute).
  const oauthFlagEnabled = useFeatureFlag('ros-oauth-enabled')
  const oauthEnabled = isRivalOAuthEnabled({
    isLocal: import.meta.env.DEV,
    rivalOAuthAvailable: config.RIVAL_OAUTH_AVAILABLE,
    featureFlagEnabled: oauthFlagEnabled,
  })
  const { areFlagsLoaded } = usePostHog()

  // One-shot acquisition (mount), deferred until flags are loaded so the gate reads the real value.
  const resolvedRef = useRef(false)
  useEffect(() => {
    if ((!config.EMAIL_CODE_AUTH_ENABLED && !areFlagsLoaded) || resolvedRef.current) return
    resolvedRef.current = true
    resolveAuth(oauthEnabled, config.EMAIL_CODE_AUTH_ENABLED).then(({ state: resolved, source }) => {
      sourceRef.current = source
      setState(resolved)
    })
  }, [areFlagsLoaded, oauthEnabled])

  // Logout in another tab writes localStorage; this tab must drop its per-tab bearer and land
  // signed-out too (the `storage` event does not fire in the tab that wrote).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== SIGNED_OUT_KEY || e.newValue !== '1') return
      try { sessionStorage.removeItem(TOKEN_KEY) } catch { /* storage unavailable */ }
      window.location.assign('/')
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // Proactive renewal: per-token timer at exp − margin + wake checks (timers don't fire in sleeping
  // tabs — on focus/visibility, renew immediately if inside the margin or past expiry).
  useEffect(() => {
    if ((sourceRef.current !== 'studio' && sourceRef.current !== 'email-code') || !state.token) return
    const delay = Math.max(tokenExpMs(state.token) - Date.now() - REFRESH_MARGIN_MS, 5_000)
    const timer = window.setTimeout(() => { void refresh() }, delay)
    const onWake = () => {
      if (document.visibilityState === 'hidden') return
      const tok = tokenRef.current
      if (tok && tokenExpMs(tok) - Date.now() < REFRESH_MARGIN_MS) void refresh()
    }
    document.addEventListener('visibilitychange', onWake)
    window.addEventListener('focus', onWake)
    return () => {
      window.clearTimeout(timer)
      if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current)
      document.removeEventListener('visibilitychange', onWake)
      window.removeEventListener('focus', onWake)
    }
  }, [state.token, refresh])

  const signIn = useCallback((): void => {
    clearSignedOut()
    redirectToRivalLogin()
  }, [])

  return { ...state, refresh, logout, signIn }
}

const TOKEN_KEY = 'ros.authToken'
const BOUNCE_KEY = 'ros.lastBounce'
const DEV_EMAIL_KEY = 'ros.devEmail'
/** Cross-tab latch: localStorage so every tab sees logout, not just the one that clicked it. */
const SIGNED_OUT_KEY = 'ros.signedOut'

function markSignedOut(): void {
  try { localStorage.setItem(SIGNED_OUT_KEY, '1') } catch { /* storage unavailable */ }
  try { sessionStorage.removeItem(TOKEN_KEY) } catch { /* storage unavailable */ }
}

export function clearSignedOut(): void {
  try { localStorage.removeItem(SIGNED_OUT_KEY) } catch { /* storage unavailable */ }
}

function isSignedOut(): boolean {
  try { return localStorage.getItem(SIGNED_OUT_KEY) === '1' } catch { return false }
}

function signedOutState(): { state: AuthState; source: AuthSource } {
  return {
    source: null,
    state: {
      token: null, email: null, name: null, userId: null, ready: true,
      needsLogin: false,
      error: "You're signed out. Sign in when you're ready.",
    },
  }
}
/** Renew this long before exp — wide enough to absorb a slow refresh, well inside the 60-min TTL. */
const REFRESH_MARGIN_MS = 120_000

/** Cognito ACCESS tokens (token_use:'access') carry no `email`/`name` claim — those live in the ID
 *  token. But the access token's `username` holds the identity; for SSO-federated users it's prefixed
 *  with the IdP, e.g. "MicrosoftEntraID_rohan@rivaltech.com". Recover a displayable email from it. */
function displayEmailFromClaims(user: Record<string, string> | null): string | null {
  if (!user) return null
  if (user.email) return user.email
  const uname = user.username || user['cognito:username']
  if (!uname) return null
  // Strip a leading "<provider>_" IdP prefix when what remains is an email (federated username).
  const stripped = uname.includes('_') ? uname.slice(uname.indexOf('_') + 1) : uname
  return /@/.test(stripped) ? stripped : uname
}

function authSourceFromToken(token: string): Exclude<AuthSource, null> {
  const user = parseJwtPayload(token)
  if (user?.iss === 'ros-next-auth') return 'email-code'
  return user?.iss === 'local-dev-auth' ? 'dev' : 'studio'
}

/** Build the resolved auth state from a JWT (used for URL, persisted, and refreshed tokens). */
function authStateFromToken(token: string): AuthState {
  const user = parseJwtPayload(token)
  const email = displayEmailFromClaims(user)
  return {
    token,
    email,
    name: user?.name || user?.given_name || email,
    userId: user?.sub || null,
    ready: true,
    error: null,
    needsLogin: false,
  }
}

/**
 * Send the browser into Ros's own Rival login (ticket 11, the app front door). The
 * backend 302s to the Rival AS; after the user signs in, the callback sets the
 * HttpOnly API session cookie and redirects to THIS app URL. Path 1c below resumes
 * that cookie-backed session, so no token is placed in the redirect URL. `returnTo`
 * is the current app URL, preserving a deep link across login.
 */
function redirectToRivalLogin(): void {
  // returnTo is where the callback sends the browser after setting its session.
  // Strip the `login` trigger so the landing does not immediately start OAuth again.
  const back = new URL(window.location.href)
  back.searchParams.delete('login')
  const returnTo = back.origin + back.pathname + back.search
  // API_CF_URL is empty locally (relative path -> Vite proxy to the backend); the
  // worker URL when deployed. Either way this reaches /auth/rival/login on reachy-cf.
  const base = config.API_CF_URL || ''
  window.location.href = `${base}/auth/rival/login?returnTo=${encodeURIComponent(returnTo)}`
}

/** The legacy (pre-OAuth) unauthenticated landing: a CTA screen prompting the user to sign in
 *  through Studio. Returned whenever the ros-oauth-enabled flag is OFF, so the app behaves exactly
 *  as it did before the OAuth front-door shipped (AppShell renders the CTA from this state). */
function legacyCtaState(): { state: AuthState; source: AuthSource } {
  return {
    source: null,
    state: {
      token: null,
      email: null,
      name: null,
      userId: null,
      ready: true,
      needsLogin: false,
      error: "Let's get you signed in. This only takes a moment.",
    },
  }
}

function loggedOutDevPickerState(): { state: AuthState; source: AuthSource } {
  return {
    source: null,
    state: {
      token: null,
      email: null,
      name: null,
      userId: null,
      ready: true,
      needsLogin: true,
      error: null,
    },
  }
}

function emailCodeLoginState(error: string | null = null): { state: AuthState; source: AuthSource } {
  return {
    source: null,
    state: {
      token: null,
      email: null,
      name: null,
      userId: null,
      ready: true,
      needsLogin: false,
      error,
    },
  }
}

async function resolveAuth(oauthEnabled: boolean, emailCodeEnabled: boolean): Promise<{ state: AuthState; source: AuthSource }> {
  const params = new URLSearchParams(window.location.search)

  // Explicit Rival login request (?login=rival): the app front door. Go straight to
  // the Rival login even where dev-login would otherwise auto-resolve, so signing in
  // through Rival is always reachable. Guard: if a `token` is also present we are the
  // post-login landing (Path 1 handles it below), so do NOT re-trigger the redirect.
  // Gated on the OAuth flag: when off, the front door does not exist, fall through to the
  // legacy CTA rather than honoring a stray ?login=rival.
  if (oauthEnabled && params.get('login') === 'rival' && !params.get('token')) {
    clearSignedOut()
    redirectToRivalLogin()
    return { source: null, state: { token: null, email: null, name: null, userId: null, ready: false, error: null, needsLogin: false } }
  }

  // Path 1: Token from URL (Studio/Rival redirect). VALIDATED before trust — Studio used to be able
  // to hand over an already-expired token (stale client cache), which made Ros look logged-in while
  // every request 401'd. Valid → persist to sessionStorage (survives refresh; we strip the URL below).
  const urlToken = params.get('token')
  if (urlToken) {
    // Remove token from URL to prevent leaking in browser history (do this regardless of validity).
    const cleanUrl = new URL(window.location.href)
    cleanUrl.searchParams.delete('token')
    window.history.replaceState({}, '', cleanUrl.toString())

    const urlSource = authSourceFromToken(urlToken)
    if (tokenStillValid(urlToken) && (!emailCodeEnabled || urlSource === 'email-code')) {
      clearSignedOut()
      try { sessionStorage.setItem(TOKEN_KEY, urlToken) } catch { /* storage unavailable — in-memory only */ }
      return { state: authStateFromToken(urlToken), source: urlSource }
    }
    // Expired handoff: DON'T auto-bounce (Studio just gave us this corpse — bouncing risks a loop);
    // fall through to dev-auth locally, or the CTA screen when deployed.
  }

  // Path 1b: Persisted token from a prior load (refresh / reopen). OAuth is cookie-sourced
  // (Path 1c): a leftover per-tab bearer must not keep this tab signed in after another tab
  // logged out. Flag-off keeps the sessionStorage path, skipping it when the user just logged out
  // so Path 2 can show the local user picker.
  if ((!oauthEnabled || emailCodeEnabled) && !isSignedOut()) {
    try {
      const stored = sessionStorage.getItem(TOKEN_KEY)
      if (stored) {
        const storedSource = authSourceFromToken(stored)
        if (tokenStillValid(stored) && (!emailCodeEnabled || storedSource === 'email-code')) {
          return { state: authStateFromToken(stored), source: storedSource }
        }
        sessionStorage.removeItem(TOKEN_KEY)
      }
    } catch { /* storage unavailable */ }
  }

  // Ros Next: the Better Auth cookie is the durable, cross-tab credential. It
  // is deliberately resolved before either Rival or Studio so these stacks are
  // self-standing and never depend on an internal service.
  if (emailCodeEnabled) {
    if (isSignedOut()) {
      try { sessionStorage.removeItem(TOKEN_KEY) } catch { /* storage unavailable */ }
      return emailCodeLoginState("You're signed out. Enter your email when you're ready.")
    }
    try {
      const res = await fetch(`${config.API_CF_URL}/auth/session`, { credentials: 'include' })
      if (res.ok) {
        const data = await res.json() as { token?: string }
        if (data.token && tokenStillValid(data.token)) {
          try { sessionStorage.setItem(TOKEN_KEY, data.token) } catch { /* storage unavailable */ }
          clearSignedOut()
          return { state: authStateFromToken(data.token), source: 'email-code' }
        }
      }
    } catch {
      // The login screen remains usable and can retry when the worker returns.
    }
    try { sessionStorage.removeItem(TOKEN_KEY) } catch { /* storage unavailable */ }
    return emailCodeLoginState()
  }

  // Explicit logout with OAuth ON: do not resume from a leftover bearer, the cookie,
  // or Rival SSO. The Welcome CTA is the signed-out landing; Sign in starts Rival.
  // Flag-off leaves this to Path 2 (dev picker) or the Studio CTA.
  if (oauthEnabled && isSignedOut()) {
    try { sessionStorage.removeItem(TOKEN_KEY) } catch { /* storage unavailable */ }
    return signedOutState()
  }

  // Path 1c: HttpOnly ros_session cookie is the cross-tab credential. A new tab (or a
  // refresh after another tab logged out) asks the worker to mint a fresh app token.
  if (oauthEnabled) {
    try {
      const res = await fetch(`${config.API_CF_URL}/auth/session`, { credentials: 'include' })
      if (res.ok) {
        const data = await res.json() as { token?: string }
        if (data.token && tokenStillValid(data.token)) {
          try { sessionStorage.setItem(TOKEN_KEY, data.token) } catch { /* storage unavailable */ }
          return { state: authStateFromToken(data.token), source: 'studio' }
        }
      }
    } catch {
      // Worker unreachable or no session: fall through to Rival login.
    }
    try { sessionStorage.removeItem(TOKEN_KEY) } catch { /* storage unavailable */ }
  }

  // Path 2: Dev auth — local development AND deployed dev stacks (dev-<name>). The worker still
  // gates /auth/dev-login on its own ENVIRONMENT=development, so this only succeeds where dev-auth
  // is actually enabled server-side.
  //
  // GATED ON !oauthEnabled: when the ros-oauth-enabled flag is ON, the dev-login shortcut is
  // deliberately skipped so the flag actually takes effect even in development. Otherwise dev-login
  // returns here and Path 3's Rival login (the whole point of the flag ON) is never reached — the
  // flag would be dead code locally. Flag OFF: dev-login behaves exactly as before.
  if (!oauthEnabled && import.meta.env.DEV) {
    // After logout (or when no email is chosen), show the picker instead of auto-logging the
    // default user. An explicit ?email= always wins and clears the logged-out latch.
    let storedDevEmail: string | null = null
    try { storedDevEmail = sessionStorage.getItem(DEV_EMAIL_KEY) } catch { /* storage unavailable */ }
    const emailParam = params.get('email')
    if (isSignedOut() || (!emailParam && !storedDevEmail)) {
      return loggedOutDevPickerState()
    }

    const devEmail = emailParam || storedDevEmail!
    try {
      // Hit the reachy-cf worker. Local: API_CF_URL is empty → relative path → Vite proxy. Deployed:
      // API_CF_URL is the worker URL (the ros-app pages.dev origin has no proxy, so it must be absolute).
      const res = await fetch(`${config.API_CF_URL}/auth/dev-login?email=${encodeURIComponent(devEmail)}&json=true`, { credentials: 'include' })
      if (res.ok) {
        const data = await res.json() as { token: string; user: { sub: string; email: string; name: string; groups: string[] } }
        try {
          sessionStorage.setItem(TOKEN_KEY, data.token)
          sessionStorage.setItem(DEV_EMAIL_KEY, data.user.email)
          clearSignedOut()
        } catch { /* storage unavailable */ }
        // Drop ?email= from the URL after a successful login so a later logout doesn't inherit it.
        if (emailParam) {
          const cleanUrl = new URL(window.location.href)
          cleanUrl.searchParams.delete('email')
          window.history.replaceState({}, '', cleanUrl.toString())
        }
        return {
          source: 'dev',
          state: {
            token: data.token,
            email: data.user.email,
            name: data.user.name,
            userId: data.user.sub,
            ready: true,
            error: null,
            needsLogin: false,
          },
        }
      }
    } catch {
      // Dev auth not available — fall through
    }

    // Fallback: no auth in dev mode (sessionId-based routing)
    return {
      source: 'dev',
      state: {
        token: null,
        email: devEmail,
        name: null,
        userId: 'dev-user',
        ready: true,
        error: null,
        needsLogin: false,
      },
    }
  }

  // Path 3: No (valid) token, not dev. When the OAuth front door is ON, this is the app
  // front door: send the user into Ros's own Rival login (ticket 11) and land them back
  // here authenticated (a not-ready pending state; the browser navigates away immediately).
  // When OFF, restore the pre-OAuth behavior exactly: return the CTA screen prompting a
  // sign-in through Studio (AppShell renders it). This is the flag's byte-identical-off path.
  if (oauthEnabled) {
    redirectToRivalLogin()
    return {
      source: null,
      state: { token: null, email: null, name: null, userId: null, ready: false, error: null, needsLogin: false },
    }
  }
  return legacyCtaState()
}

/** Called by the login picker: clear the logout latch and sign in as the chosen user. */
export function continueDevLoginAs(email: string): void {
  clearSignedOut()
  try { sessionStorage.setItem(DEV_EMAIL_KEY, email) } catch { /* storage unavailable */ }
  window.location.href = `/?email=${encodeURIComponent(email)}`
}
